import he from 'he';

/**
 * Heuristic HTML detector: a real tag wins regardless of newlines (editor/paste HTML may
 * contain inter-tag newlines); entity-only values also pass through, but only when tag-less
 * and newline-free (guards "Q&A;\n…" from being misread as an entity reference).
 *
 * Keep in sync with wobo-okr `normalizeStoredValueToHtml` and rbr `looksLikeCustomFieldHtml`.
 * All tag patterns here must use `[^<>]` (never `[^>]`) — `[^>]` can cross `<` and
 * reintroduces O(n²). Closer/rooted gates have no open character class.
 */
const HTML_BARE_TAG = /<\/?[a-z][a-z0-9-]*\s*\/?>/i;
const HTML_ATTR_TAG = /<\/?[a-z][a-z0-9-]*\s[^<>]*>/i;
const HTML_VOID_TAG = /<(?:br|hr|img)\b[^<>]*>/i;
const HTML_ENTITY_PATTERN = /&(#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/i;

function looksLikeHtml(value: string): boolean {
  // Cheap incomplete-tag skip (not the ReDoS defense — patterns above are linear).
  if (!value.includes('>')) {
    if (/[\r\n]/.test(value)) return false;
    return HTML_ENTITY_PATTERN.test(value);
  }
  if (HTML_BARE_TAG.test(value) || HTML_ATTR_TAG.test(value)) {
    // Second gate: bare/attr regex alone matches comparison prose — only rooted markup,
    // a closer, or a void tag counts as HTML (not orphan mid-prose `<b>`).
    if (
      /^\s*</.test(value) ||
      /<\/[a-z][a-z0-9-]*>/i.test(value) ||
      HTML_VOID_TAG.test(value)
    ) {
      return true;
    }
  }
  // Tag-less but has a newline => plain text (keeps the "Q&A;\n…" case from being misread as an entity).
  if (/[\r\n]/.test(value)) return false;
  return HTML_ENTITY_PATTERN.test(value);
}

/**
 * Converts a stored custom-field value into HTML safe to hand to `HtmlInput`/`HtmlContent`.
 * Already-HTML values pass through unchanged (sanitized downstream by HtmlContent/DOMPurify,
 * round-tripped by HtmlInput's deserializer). Plain text is entity-escaped and its newlines
 * become `<br>` so existing line breaks are not lost.
 *
 * Pass-through is unsanitized by design — HtmlContent DOMPurifies downstream.
 * Encode-before-`<br>` is load-bearing: reverse the order and stored XSS lands.
 */
export function normalizeStoredValueToHtml(value: string | null | undefined): string {
  if (!value) return '';
  if (looksLikeHtml(value)) return value;
  return he
    .encode(value, { useNamedReferences: true, encodeEverything: false })
    .replace(/\r\n|\r|\n/g, '<br>');
}
